import type {
  ApiResponse,
  PaginatedResponse,
  HoldingResponse,
  SystemStatus,
  PortfolioResponse,
  PortfolioSummary,
  StockPosition,
  TradeRecord,
  TradePreset,
  TradePresetInput,
  PresetStage,
  OrderbookData,
  ChartCandle,
  PerformanceItem,
  ResplitStage,
  ResplitPreviewResponse,
  AutoTradeMode,
} from "@/types";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit,
  timeoutMs: number = 30000
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${API_BASE}${endpoint}`, {
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
      ...options,
      signal: controller.signal,
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.detail || `HTTP ${res.status}`);
    }

    return res.json();
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error("API 요청 타임아웃");
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

export const api = {
  // Holding
  getHolding: () =>
    fetchApi<ApiResponse<HoldingResponse>>("/api/holding/summary"),
  getStatus: () =>
    fetchApi<ApiResponse<SystemStatus>>("/api/holding/status"),

  // Portfolio
  getPortfolioList: () =>
    fetchApi<ApiResponse<PortfolioSummary[]>>("/api/portfolio/list"),
  getPortfolio: (name?: string) =>
    fetchApi<ApiResponse<PortfolioResponse>>(
      name ? `/api/portfolio?name=${encodeURIComponent(name)}` : "/api/portfolio"
    ),
  getStocks: (name?: string) =>
    fetchApi<ApiResponse<StockPosition[]>>(
      name ? `/api/portfolio/stocks?name=${encodeURIComponent(name)}` : "/api/portfolio/stocks"
    ),

  // Trades
  getTrades: (params: Record<string, string>) =>
    fetchApi<PaginatedResponse<TradeRecord>>(
      `/api/trades?${new URLSearchParams(params)}`
    ),

  // Config
  getConfig: () =>
    fetchApi<ApiResponse<Record<string, unknown>>>("/api/config"),
  updateConfigSection: <T>(section: string, data: T) =>
    fetchApi<ApiResponse<T>>(`/api/config/${section}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  // Portfolio yaml 의 reserve 섹션 수정 (code 빈 문자열 → 섹션 삭제)
  updatePortfolioReserve: (
    portfolioName: string,
    data: {
      code: string;
      name?: string;
      enabled?: boolean;
      target_cash_ratio?: number;
      cash_buffer?: number;
      buy_max_ratio?: number;
      buy_at?: string;
    },
  ) =>
    fetchApi<ApiResponse<{ reserve: unknown }>>(
      `/api/config/reserve?name=${encodeURIComponent(portfolioName)}`,
      { method: "PUT", body: JSON.stringify(data) },
    ),

  // Presets
  getPresets: () =>
    fetchApi<ApiResponse<TradePreset[]>>("/api/presets"),
  getPreset: (id: number) =>
    fetchApi<ApiResponse<TradePreset>>(`/api/presets/${id}`),
  createPreset: (data: TradePresetInput) =>
    fetchApi<ApiResponse<TradePreset>>("/api/presets", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updatePreset: (id: number, data: Partial<TradePresetInput>) =>
    fetchApi<ApiResponse<TradePreset>>(`/api/presets/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  deletePreset: (id: number) =>
    fetchApi<ApiResponse<null>>(`/api/presets/${id}`, { method: "DELETE" }),

  // Orderbook & Chart
  getOrderbook: (name: string, code: string) =>
    fetchApi<ApiResponse<OrderbookData>>(`/api/portfolio/orderbook?name=${encodeURIComponent(name)}&code=${encodeURIComponent(code)}`),
  getChart: (name: string, code: string, period: string = "D") =>
    fetchApi<ApiResponse<ChartCandle[]>>(`/api/portfolio/chart?name=${encodeURIComponent(name)}&code=${encodeURIComponent(code)}&period=${period}`),
  placeOrder: (data: { portfolio_name: string; code: string; side: string; qty: number; price: number; stage?: number; order_type?: "limit" | "market" | "mid"; skip_amount_limit?: boolean }) =>
    fetchApi<ApiResponse<{ success: boolean; order_no: string; message: string }>>("/api/portfolio/order", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  cancelOrder: (data: { portfolio_name: string; order_no: string; code: string; qty: number; side?: string }) =>
    fetchApi<ApiResponse<{ success: boolean; message: string }>>("/api/portfolio/cancel-order", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  modifyOrder: (data: { portfolio_name: string; order_no: string; code: string; qty: number; price: number }) =>
    fetchApi<ApiResponse<{ success: boolean; message: string }>>("/api/portfolio/modify-order", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Stocks
  createStock: (data: { portfolio_name: string; code: string; name: string }) =>
    fetchApi<ApiResponse<unknown>>("/api/stocks", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  updateStockTradeMode: (portfolioName: string, code: string, tradeMode: string) =>
    fetchApi<ApiResponse<unknown>>(`/api/stocks/by-code/${encodeURIComponent(portfolioName)}/${encodeURIComponent(code)}/trade-mode`, {
      method: "PUT",
      body: JSON.stringify({ trade_mode: tradeMode }),
    }),
  updateStockMaxStage: (portfolioName: string, code: string, maxStage: number) =>
    fetchApi<ApiResponse<unknown>>(`/api/stocks/by-code/${encodeURIComponent(portfolioName)}/${encodeURIComponent(code)}/max-stage`, {
      method: "PUT",
      body: JSON.stringify({ max_stage: maxStage }),
    }),
  updateStockFavorite: (portfolioName: string, code: string, favorite: boolean) =>
    fetchApi<ApiResponse<unknown>>(`/api/stocks/by-code/${encodeURIComponent(portfolioName)}/${encodeURIComponent(code)}/favorite`, {
      method: "PUT",
      body: JSON.stringify({ favorite }),
    }),
  updateStockEnabled: (portfolioName: string, code: string, enabled: boolean) =>
    fetchApi<ApiResponse<unknown>>(`/api/stocks/by-code/${encodeURIComponent(portfolioName)}/${encodeURIComponent(code)}/enabled`, {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    }),
  updateStockPreset: (portfolioName: string, code: string, presetId: number | null) =>
    fetchApi<ApiResponse<unknown>>(`/api/stocks/by-code/${encodeURIComponent(portfolioName)}/${encodeURIComponent(code)}/preset`, {
      method: "PUT",
      body: JSON.stringify({ trade_preset_id: presetId }),
    }),
  getCustomStages: (portfolioName: string, code: string) =>
    fetchApi<ApiResponse<PresetStage[]>>(`/api/stocks/by-code/${encodeURIComponent(portfolioName)}/${encodeURIComponent(code)}/custom-stages`),
  saveCustomStages: (portfolioName: string, code: string, stages: PresetStage[]) =>
    fetchApi<ApiResponse<{ customized: boolean }>>(`/api/stocks/by-code/${encodeURIComponent(portfolioName)}/${encodeURIComponent(code)}/custom-stages`, {
      method: "PUT",
      body: JSON.stringify({ stages }),
    }),

  // System
  getAutoTrade: (portfolioName?: string) =>
    fetchApi<ApiResponse<{ portfolio?: string; mode: AutoTradeMode; enabled: boolean }>>(
      `/api/system/auto-trade${portfolioName ? `?name=${encodeURIComponent(portfolioName)}` : ""}`
    ),
  setAutoTrade: (mode: AutoTradeMode, portfolioName?: string) =>
    fetchApi<ApiResponse<{ portfolio?: string; mode: AutoTradeMode; enabled: boolean }>>(
      `/api/system/auto-trade${portfolioName ? `?name=${encodeURIComponent(portfolioName)}` : ""}`,
      {
        method: "POST",
        body: JSON.stringify({ mode }),
      }
    ),
  startTrader: () =>
    fetchApi<ApiResponse<null>>("/api/system/start", { method: "POST" }),
  stopTrader: () =>
    fetchApi<ApiResponse<null>>("/api/system/stop", { method: "POST" }),
  restartTrader: () =>
    fetchApi<ApiResponse<null>>("/api/system/restart", { method: "POST" }),
  reconcileState: (name: string) =>
    fetchApi<ApiResponse<{
      success: boolean;
      message: string;
      applied: Array<{ code: string; old_qty: number; new_qty: number; stages: [number, number][] }>;
      cleared: Array<{ code: string; old_qty: number }>;
      skipped: Array<{ code: string; state_qty: number; broker_qty: number; reason: string }>;
    }>>(`/api/system/reconcile-state?name=${encodeURIComponent(name)}`, { method: "POST" }),

  // 포트폴리오 이름 변경 (DB + yaml + state + auto_trade 파일 일괄)
  renamePortfolio: (oldName: string, newName: string) =>
    fetchApi<ApiResponse<{
      success: boolean;
      old: string;
      new: string;
      db_rows: Record<string, number>;
      files: string[];
      warnings: string[];
      message?: string;
    }>>(
      `/api/system/rename-portfolio?old=${encodeURIComponent(oldName)}&new=${encodeURIComponent(newName)}`,
      { method: "POST" },
    ),

  // Resplit
  resplitPreview: (data: {
    portfolio_name: string;
    code: string;
    num_stages: number;
    price_gap_pct: number;
    price_gap_type: string;
    price_gap_amount: number;
    base_price: number;
    preserve_total_cost: boolean;
    preserve_strategy?: "uniform" | "first_only";
    exclude_first_stage?: boolean;
  }) =>
    fetchApi<ApiResponse<ResplitPreviewResponse>>("/api/portfolio/resplit/preview", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  resplitExecute: (data: { portfolio_name: string; code: string; stages: ResplitStage[]; preserve_total_cost?: boolean }) =>
    fetchApi<ApiResponse<{ success: boolean; message: string; new_max_stage?: number }>>("/api/portfolio/resplit/execute", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Performance
  getPerformance: (name: string) =>
    fetchApi<ApiResponse<PerformanceItem[]>>(`/api/performance?name=${encodeURIComponent(name)}`),
};

// SWR fetcher
export const fetcher = <T>(url: string, timeoutMs?: number) => fetchApi<T>(url, undefined, timeoutMs);
